import * as React from "react";

import { Link, useTranslation, Trans, I18nextContext } from "gatsby-plugin-react-i18next";
import { StaticImage } from "gatsby-plugin-image";

import FacebookIcon from "../../images/icons/facebook.svg";
import InstagramIcon from "../../images/icons/instagram.svg";
import LinkedInIcon from "../../images/icons/linkedin.svg";
import YoutubeIcon from "../../images/icons/youtube.svg";
import PoweredByIcon from "../../images/vivacom/powered_by_boleron.svg";
import "@styles/components/Footer.sass";
import { getActiveProductsFromConfig, getFooter, getFooterLogo } from "../../config/activeProducts/helpers";
import ProductsService from "../../services/ProductsService";
import { GATSBY_CONFIGURATION } from "../../config/activeProducts";

import { modifyProductsForFooter } from "../../utils/functions";
import BoleronEuBanner from "../common/BoleronEuBanner";

const Footer = () => {
  const currentLanguage = React.useContext(I18nextContext).language;

  const { t } = useTranslation();

  const Logo = getFooterLogo();

  const activeProducts = GATSBY_CONFIGURATION === "boleron" ? modifyProductsForFooter(getActiveProductsFromConfig()) : getActiveProductsFromConfig();

  const chunkIntoColumns = (arr, numColumns) => {
    const columns = Array.from({ length: numColumns }, () => []);
    arr.forEach((item, index) => {
      columns[index % numColumns].push(item);
    });
    return columns;
  };

  const productColumns = chunkIntoColumns(activeProducts, 3);

  return (
    <footer className="footer-new">
      <div className="container-main">
        <div className="columns is-column-on-mobile">
          <div className="logo-with-info is-one-third">
            <div className="column column-specification first-column pt-0">
              <div className="navbar-brand">
                <Link className="navbar-item is-block pl-0 pt-0 pb-5" to="/">
                  {Logo}
                  {!getFooter()?.logoText?.disabled && (
                    <div
                      className="is-uppercase text-logo-footer"
                      style={{
                        letterSpacing: { bg: "1px", en: "2.8px", ru: "3.5px" }[currentLanguage],
                        paddingLeft: 8,
                        textAlign: "center",
                        fontSize: 12
                      }}
                    >
                      {t("online_insurance", "Онлайн застраховки")}
                    </div>
                  )}
                </Link>
              </div>

              <div className="contact-info-wrapper pl-0 pt-0 pb-2">
                {getFooter()?.phone?.text && (
                  <div className="contact-info">
                    {getFooter()?.phone?.link && (
                      <a className="email-footer is-size-3-5" href={`tel:${getFooter()?.phone?.link}`}>
                        <span>{getFooter()?.phone?.text}</span>
                      </a>
                    )}
                    {!getFooter()?.phone?.link && (
                      <a className="email-footer is-size-3-5" href={`tel:${getFooter()?.phone?.text}`}>
                        <span>{getFooter()?.phone?.text}</span>
                      </a>
                    )}
                  </div>
                )}
                <div className="contact-info">
                  <a className="email-footer is-size-3-5" href={`mailto:${getFooter()?.email?.text}`}>
                    {getFooter()?.email?.text}
                  </a>
                </div>
              </div>

              {!getFooter()?.isoSection?.disabled && (
                <>
                  <div className="column-with-social-media pb-5 is-hidden-touch">
                    <div className="contact-icons-wrapper-footer">
                      <a href="https://www.facebook.com/boleron.bg/" target="_blank" rel="noreferrer" aria-label="Boleron във Facebook">
                        <FacebookIcon className="contact-icons-footer" />
                      </a>
                      <a href="https://www.instagram.com/boleron.bg/" target="_blank" rel="noreferrer" aria-label="Boleron в Instagram">
                        <InstagramIcon className="contact-icons-footer" />
                      </a>
                      <a href="https://www.linkedin.com/company/boleron" target="_blank" rel="noreferrer" aria-label="Boleron в LinkedIn">
                        <LinkedInIcon className="contact-icons-footer" />
                      </a>
                      <a href="https://www.youtube.com/channel/UC-OkJILsTbjN8c6yjUhaPQg" target="_blank" rel="noreferrer" aria-label="Boleron в YouTube">
                        <YoutubeIcon className="contact-icons-footer contact-youtube-icon" />
                      </a>
                    </div>
                  </div>
                  <div className="is-flex column-gap-12 is-hidden-touch">
                    <StaticImage
                      src="../../../static/images/boleron/ISO_9001.png"
                      alt="Сертификат ISO9001"
                      width={69}
                      height={81}
                      loading="lazy"
                      quality={80}
                      formats={["avif", "webp"]}
                      placeholder="none"
                    />
                    <StaticImage
                      src="../../../static/images/boleron/ISO_27001.png"
                      alt="Сертификат ISO27001"
                      width={69}
                      height={81}
                      loading="lazy"
                      quality={80}
                      formats={["avif", "webp"]}
                      placeholder="none"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          <div>
            <div className="columns column-specification is-flex-direction-row" style={{ gap: "30px", marginBottom: 0 }}>
              <div className="column ">
                {!getFooter()?.isoSection?.disabled && (
                  <div className="mb-4">
                    <Link className="link-in-footer is-white-space-nowrap is-regular-font-family" to="/about-us/">
                      {t("AboutUs", "За Нас")}
                    </Link>
                  </div>
                )}

                <div className="mb-4">
                  <Link className="link-in-footer is-white-space-nowrap  is-regular-font-family" to="/documents/">
                    {t("Useful information", "Полезна информация")}
                  </Link>
                </div>

                {!getFooter()?.isoSection?.disabled && (
                  <div className="mb-4">
                    <Link className="link-in-footer is-white-space-nowrap  is-regular-font-family" to="/contacts/">
                      {t("Contacts", "Контакти")}
                    </Link>
                  </div>
                )}
              </div>

              {productColumns.map((col, colIndex) => (
                <div className="column" key={colIndex}>
                  {col.map(p => (
                    <div key={p.id} className="mb-4">
                      <Link className="link-in-footer is-white-space-nowrap is-regular-font-family" to={p.link}>
                        {ProductsService.getProductName(p.id, currentLanguage)}
                      </Link>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {GATSBY_CONFIGURATION === "boleron" && <BoleronEuBanner />}

            {!getFooter()?.isoSection?.disabled && (
              <>
                <div className="footer-message-container">
                  <Trans i18nKey="Footer Address Slogan" />
                  <Trans i18nKey="Footer Address" />
                </div>

                <div className="footer-message-container mt-3">
                  <Trans i18nKey="Footer ISO" />
                </div>
              </>
            )}

            {!getFooter()?.hr?.disabled && (
              <div className={`${getFooter()?.footerText?.isCentered ? "has-text-centered" : ""}  ${getFooter()?.footerText?.paddingStyles}`}>
                Застрахователните продукти и услуги се предоставят чрез посредничеството на Виваком България ЕАД, ЕИК: 831642181 - застрахователен брокер с номер на удостоверение
                552-ЗБ от 18.10.2022 г. и Болерон Консултинг ЕООД, ЕИК: 102764975 - застрахователен брокер с номер на удостоверение 721-3Б от 01.12.2020 г., издадени от Комисията
                по финансов надзор.
              </div>
            )}
          </div>
        </div>
      </div>

      {!getFooter()?.isoSection?.disabled && (
        <div className="is-flex column-gap-12 is-hidden-desktop is-justify-content-center mt-5">
          <StaticImage src="../../../static/images/boleron/ISO_9001.png" alt="ISO9001" width={69} height={81} loading="lazy" quality={90} placeholder="blurred" />
          <StaticImage src="../../../static/images/boleron/ISO_27001.png" alt="ISO27001" width={69} height={81} loading="lazy" quality={90} placeholder="blurred" />
        </div>
      )}

      {!getFooter()?.sectionSocialMedia?.disabled && (
        <div className="mobile-social-media-icons">
          <div className="column-with-social-media">
            <div className="contact-icons-wrapper-footer">
              <a href="https://www.facebook.com/boleron.bg/" target="_blank" rel="noreferrer" aria-label="Boleron във Facebook">
                <FacebookIcon className="contact-icons-footer" />
              </a>
              <a href="https://www.instagram.com/boleron.bg/" target="_blank" rel="noreferrer" aria-label="Boleron в Instagram">
                <InstagramIcon className="contact-icons-footer" />
              </a>
              <a href="https://www.linkedin.com/company/boleron" target="_blank" rel="noreferrer" aria-label="Boleron в LinkedIn">
                <LinkedInIcon className="contact-icons-footer" />
              </a>
              <a href="https://www.youtube.com/channel/UC-OkJILsTbjN8c6yjUhaPQg" target="_blank" rel="noreferrer" aria-label="Boleron в YouTube">
                <YoutubeIcon className="contact-icons-footer contact-youtube-icon" />
              </a>
            </div>
          </div>
        </div>
      )}

      {!getFooter()?.hr?.disabled && <hr style={{ maginTop: 0 }} />}
      {!getFooter()?.hr?.disabled && (
        <div className="container-main">
          <div className="powered-by-boleron is-flex is-align-items-center">
            <a href="https://www.boleron.bg">
              <PoweredByIcon className="icon-footer ml-2 mr-2" /> <span>Powered by Boleron</span>
            </a>
          </div>
        </div>
      )}
    </footer>
  );
};

export default Footer;
